import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { deadlinesAPI, clientsAPI, projectsAPI } from '../lib/api';
import { formatDate, formatCurrency, getRelativeTime, getRecurrenceLabel, isOverdue, isWithinDays } from '../lib/utils';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Plus,
  CalendarIcon,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarClock,
  Globe,
  FileText,
  ChevronLeft,
  ChevronRight,
  Pencil,
  RotateCcw,
  Euro,
  TrendingUp,
  Filter,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addMonths, isSameMonth } from 'date-fns';
import { it } from 'date-fns/locale';

const deadlineSchema = z.object({
  titolo: z.string().min(2, 'Titolo richiesto'),
  descrizione: z.string().optional(),
  data_scadenza: z.date({ required_error: 'Data richiesta' }),
  importo: z.coerce.number().min(0).optional().nullable(),
  client_id: z.string().optional(),
  project_id: z.string().optional(),
  ricorrenza: z.enum(['mensile', 'trimestrale', 'semestrale', 'annuale']).optional().nullable(),
  promemoria_giorni: z.coerce.number().min(0).default(7),
});

const STATUS_FILTERS = [
  { value: 'all',      label: 'Tutte' },
  { value: 'overdue',  label: 'Scadute',   dot: 'bg-red-500' },
  { value: 'upcoming', label: 'Imminenti', dot: 'bg-amber-500' },
  { value: 'completed',label: 'Saldate',   dot: 'bg-emerald-500' },
];

export const Scadenziario = () => {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0); // default: questo mese

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(deadlineSchema),
    defaultValues: { promemoria_giorni: 7 },
  });

  const selectedClient = watch('client_id');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (selectedClient) {
      projectsAPI.getAll(selectedClient).then(res => setProjects(res.data));
    }
  }, [selectedClient]);

  const fetchData = async () => {
    try {
      const [deadlinesRes, clientsRes] = await Promise.all([
        deadlinesAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setDeadlines(deadlinesRes.data);
      setClients(clientsRes.data);
    } catch {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        data_scadenza: data.data_scadenza?.toISOString(),
        importo: data.importo || null,
        client_id: data.client_id || null,
        project_id: data.project_id || null,
        ricorrenza: data.ricorrenza || null,
      };
      if (editingDeadline) {
        await deadlinesAPI.update(editingDeadline.id, payload);
        toast.success('Scadenza aggiornata');
      } else {
        await deadlinesAPI.create(payload);
        toast.success('Scadenza creata');
      }
      fetchData();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingDeadline(null);
    reset({ promemoria_giorni: 7 });
  };

  const handleEdit = (deadline) => {
    setEditingDeadline(deadline);
    reset({
      titolo: deadline.titolo,
      descrizione: deadline.descrizione || '',
      data_scadenza: new Date(deadline.data_scadenza),
      importo: deadline.importo ?? null,
      client_id: deadline.client_id || undefined,
      project_id: deadline.project_id || undefined,
      ricorrenza: deadline.ricorrenza || undefined,
      promemoria_giorni: deadline.promemoria_giorni ?? 7,
    });
    setDialogOpen(true);
  };

  const handleComplete = async (deadline) => {
    try {
      await deadlinesAPI.complete(deadline.id);
      toast.success((deadline.importo || 0) > 0 ? 'Segnata come saldata' : 'Scadenza completata');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const handleReopen = async (id) => {
    try {
      await deadlinesAPI.reopen(id);
      toast.success('Scadenza riaperta');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa scadenza?')) return;
    try {
      await deadlinesAPI.delete(id);
      toast.success('Scadenza eliminata');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.nome || null;

  const getDeadlineStatus = (deadline) => {
    if (deadline.completata) {
      return {
        label: (deadline.importo || 0) > 0 ? 'Saldato' : 'Completata',
        color: 'bg-emerald-100 text-emerald-700',
        border: 'border-l-emerald-400',
        icon: CheckCircle2,
      };
    }
    const threshold = deadline.promemoria_giorni ?? 7;
    if (isOverdue(deadline.data_scadenza)) return { label: 'Scaduta', color: 'bg-red-100 text-red-700', border: 'border-l-red-400', icon: AlertTriangle };
    if (isWithinDays(deadline.data_scadenza, threshold)) return { label: 'Imminente', color: 'bg-amber-100 text-amber-700', border: 'border-l-amber-400', icon: Clock };
    return { label: 'In scadenza', color: 'bg-slate-100 text-slate-600', border: 'border-l-slate-300', icon: CalendarClock };
  };

  // Periodo selezionato
  const selectedMonth = addMonths(new Date(), monthOffset);
  const isAllMode = monthOffset === null;

  // Base statistica: rispetta sia il filtro cliente che il mese
  const statsBase = deadlines
    .filter(d => clientFilter === 'all' || d.client_id === clientFilter)
    .filter(d => isAllMode || isSameMonth(new Date(d.data_scadenza), selectedMonth));

  // KPI conteggio
  const overdueCount  = statsBase.filter(d => !d.completata && isOverdue(d.data_scadenza)).length;
  const upcomingCount = statsBase.filter(d => !d.completata && isWithinDays(d.data_scadenza, d.promemoria_giorni ?? 7) && !isOverdue(d.data_scadenza)).length;
  const completedCount = statsBase.filter(d => d.completata).length;

  // KPI finanziarie
  const previstoIncasso    = statsBase.filter(d => !d.completata && (d.importo || 0) > 0).reduce((s, d) => s + (d.importo || 0), 0);
  const incassatoNelPeriodo = statsBase.filter(d => d.completata && (d.importo || 0) > 0).reduce((s, d) => s + (d.importo || 0), 0);
  const scadutoNonSaldato  = statsBase.filter(d => !d.completata && (d.importo || 0) > 0 && isOverdue(d.data_scadenza)).reduce((s, d) => s + (d.importo || 0), 0);
  const totaleFinanziario  = previstoIncasso + incassatoNelPeriodo;
  const pctIncassato       = totaleFinanziario > 0 ? Math.round((incassatoNelPeriodo / totaleFinanziario) * 100) : 0;
  const hasFinancial       = totaleFinanziario > 0 || scadutoNonSaldato > 0;

  // Lista filtrata
  const filteredDeadlines = deadlines
    .filter(d => showCompleted || !d.completata)
    .filter(d => clientFilter === 'all' || d.client_id === clientFilter)
    .filter(d => {
      if (filter === 'all') return true;
      if (filter === 'overdue')  return !d.completata && isOverdue(d.data_scadenza);
      if (filter === 'upcoming') return !d.completata && isWithinDays(d.data_scadenza, d.promemoria_giorni ?? 7);
      if (filter === 'completed') return d.completata;
      return true;
    })
    .filter(d => isAllMode || isSameMonth(new Date(d.data_scadenza), selectedMonth))
    .sort((a, b) => {
      if (a.completata !== b.completata) return a.completata ? 1 : -1;
      return new Date(a.data_scadenza) - new Date(b.data_scadenza);
    });

  const periodoLabel = isAllMode
    ? 'tutto il periodo'
    : format(selectedMonth, 'MMMM yyyy', { locale: it });

  return (
    <Layout title="Scadenziario">
      <div className="space-y-5">

        {/* ── Barra superiore: filtri + azione ── */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                <SelectValue placeholder="Tutti i clienti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Checkbox id="show-completed" checked={showCompleted} onCheckedChange={setShowCompleted} />
              <label htmlFor="show-completed" className="text-sm text-slate-600 cursor-pointer">Mostra saldate</label>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-deadline-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuova Scadenza
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingDeadline ? 'Modifica Scadenza' : 'Nuova Scadenza'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input {...register('titolo')} data-testid="deadline-titolo-input" />
                  {errors.titolo && <p className="text-sm text-red-500">{errors.titolo.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Euro className="h-3.5 w-3.5" /> Importo (€)</Label>
                    <Input type="number" step="0.01" placeholder="Opzionale" {...register('importo')} />
                    <p className="text-xs text-slate-400">Per tracciare incassi previsti</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Promemoria (giorni prima)</Label>
                    <Input type="number" {...register('promemoria_giorni')} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Data Scadenza *</Label>
                  <Controller
                    name="data_scadenza"
                    control={control}
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start font-normal" data-testid="deadline-data-btn">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : 'Seleziona data'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={field.value} onSelect={field.onChange} locale={it} />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.data_scadenza && <p className="text-sm text-red-500">{errors.data_scadenza.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cliente</Label>
                    <Controller
                      name="client_id"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || 'none'}>
                          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuno</SelectItem>
                            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Progetto</Label>
                    <Controller
                      name="project_id"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || 'none'}>
                          <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nessuno</SelectItem>
                            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Ricorrenza</Label>
                  <Controller
                    name="ricorrenza"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value || 'none'}>
                        <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna</SelectItem>
                          <SelectItem value="mensile">Mensile</SelectItem>
                          <SelectItem value="trimestrale">Trimestrale</SelectItem>
                          <SelectItem value="semestrale">Semestrale</SelectItem>
                          <SelectItem value="annuale">Annuale</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea {...register('descrizione')} rows={2} />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={closeDialog}>Annulla</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-deadline-btn">
                    {editingDeadline ? 'Salva' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* ── Navigatore mese ── */}
        <div className="flex items-center gap-1 bg-white border rounded-xl px-3 py-2 overflow-x-auto">
          <button
            onClick={() => setMonthOffset(o => o - 1)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-1 flex-1 justify-center flex-wrap">
            {[-2, -1, 0, 1, 2, 3].map((offset) => {
              const m = addMonths(new Date(), offset);
              const label = offset === 0 ? 'Questo mese'
                : offset === -1 ? 'Scorso'
                : format(m, 'MMM yyyy', { locale: it });
              const isSelected = monthOffset === offset;
              const hasOverdue = deadlines.some(d =>
                !d.completata && isOverdue(d.data_scadenza) && isSameMonth(new Date(d.data_scadenza), m)
              );
              return (
                <button
                  key={offset}
                  onClick={() => setMonthOffset(offset)}
                  className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize whitespace-nowrap ${
                    isSelected ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {label}
                  {hasOverdue && !isSelected && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setMonthOffset(o => o + 1)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors shrink-0"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* ── KPI Previsione incasso ── */}
        {hasFinancial ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Previsto */}
            <Card className="p-5 border-l-4 border-l-indigo-400">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Previsto incasso</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">{formatCurrency(previstoIncasso)}</p>
                  <p className="text-xs text-slate-400 mt-1 capitalize">{periodoLabel}</p>
                </div>
                <div className="p-2.5 rounded-xl bg-indigo-100">
                  <TrendingUp className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
              {/* Barra progressione */}
              {totaleFinanziario > 0 && (
                <div className="mt-4 space-y-1.5">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Incassato: {pctIncassato}%</span>
                    <span>{formatCurrency(incassatoNelPeriodo)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${pctIncassato}%` }}
                    />
                  </div>
                </div>
              )}
            </Card>

            {/* Già incassato */}
            <Card className="p-5 border-l-4 border-l-emerald-400">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Già incassato</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{formatCurrency(incassatoNelPeriodo)}</p>
                  <p className="text-xs text-slate-400 mt-1">{completedCount} scadenze saldate</p>
                </div>
                <div className="p-2.5 rounded-xl bg-emerald-100">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </Card>

            {/* Scaduto non saldato */}
            <Card className={`p-5 border-l-4 ${scadutoNonSaldato > 0 ? 'border-l-red-400' : 'border-l-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Scaduto non saldato</p>
                  <p className={`text-2xl font-bold mt-1 ${scadutoNonSaldato > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                    {formatCurrency(scadutoNonSaldato)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">{overdueCount} scadenze in ritardo</p>
                </div>
                <div className={`p-2.5 rounded-xl ${scadutoNonSaldato > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <AlertTriangle className={`h-5 w-5 ${scadutoNonSaldato > 0 ? 'text-red-600' : 'text-slate-300'}`} />
                </div>
              </div>
            </Card>
          </div>
        ) : (
          /* Contatori semplici quando non ci sono importi */
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Totale', value: statsBase.length, color: '' },
              { label: 'Scadute', value: overdueCount, color: overdueCount > 0 ? 'text-red-600' : '' },
              { label: 'Imminenti', value: upcomingCount, color: upcomingCount > 0 ? 'text-amber-600' : '' },
              { label: 'Completate', value: completedCount, color: 'text-emerald-600' },
            ].map(({ label, value, color }) => (
              <Card key={label} className="p-4 text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
                <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
              </Card>
            ))}
          </div>
        )}

        {/* ── Filtro stato (pills) + mini contatori ── */}
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_FILTERS.map(f => {
            const count = f.value === 'all' ? filteredDeadlines.length
              : f.value === 'overdue'  ? overdueCount
              : f.value === 'upcoming' ? upcomingCount
              : completedCount;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  filter === f.value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {f.dot && <span className={`w-2 h-2 rounded-full ${filter === f.value ? 'bg-white/70' : f.dot}`} />}
                {f.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === f.value ? 'bg-white/20' : 'bg-slate-100'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Lista scadenze ── */}
        <div className="space-y-2" data-testid="deadlines-list">
          {loading ? (
            <Card className="p-8 text-center"><div className="spinner mx-auto" /></Card>
          ) : filteredDeadlines.length === 0 ? (
            <Card className="p-10 text-center text-slate-400">
              <CalendarClock className="h-10 w-10 mx-auto mb-3 text-slate-200" />
              <p>Nessuna scadenza per questo periodo</p>
            </Card>
          ) : (
            filteredDeadlines.map((deadline) => {
              const status = getDeadlineStatus(deadline);
              const StatusIcon = status.icon;
              const iconColor = status.color.includes('emerald') ? 'text-emerald-500'
                : status.color.includes('red') ? 'text-red-500'
                : status.color.includes('amber') ? 'text-amber-500'
                : 'text-slate-300';

              return (
                <Card
                  key={deadline.id}
                  className={`border-l-4 ${status.border} transition-all ${deadline.completata ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Icona stato / bottone completa */}
                    <button
                      onClick={() => !deadline.completata && handleComplete(deadline)}
                      disabled={deadline.completata}
                      title={deadline.completata ? status.label : ((deadline.importo || 0) > 0 ? 'Segna come saldato' : 'Segna come completata')}
                      className={`shrink-0 ${!deadline.completata ? 'hover:scale-110 transition-transform' : ''}`}
                    >
                      <StatusIcon className={`h-6 w-6 ${iconColor}`} />
                    </button>

                    {/* Contenuto principale */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${deadline.completata ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                          {deadline.titolo}
                        </span>
                        <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
                      </div>

                      {deadline.descrizione && (
                        <p className="text-sm text-slate-500 mt-0.5 truncate">{deadline.descrizione}</p>
                      )}

                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 flex-wrap">
                        <span className="font-medium">{formatDate(deadline.data_scadenza)}</span>
                        {!deadline.completata && (
                          <span className={`${isOverdue(deadline.data_scadenza) ? 'text-red-500 font-semibold' : isWithinDays(deadline.data_scadenza, deadline.promemoria_giorni ?? 7) ? 'text-amber-500 font-medium' : ''}`}>
                            {getRelativeTime(deadline.data_scadenza)}
                          </span>
                        )}
                        {getClientName(deadline.client_id) && (
                          <span className="text-slate-500">• {getClientName(deadline.client_id)}</span>
                        )}
                        {deadline.ricorrenza && (
                          <Badge variant="outline" className="text-xs">{getRecurrenceLabel(deadline.ricorrenza)}</Badge>
                        )}
                        {deadline.domain_id && (
                          <Badge variant="outline" className="text-xs gap-1 text-indigo-600 border-indigo-200 cursor-pointer hover:bg-indigo-50" onClick={() => navigate('/domini')}>
                            <Globe className="h-3 w-3" /> Dominio
                          </Badge>
                        )}
                        {deadline.contract_id && (
                          <Badge variant="outline" className="text-xs gap-1 text-purple-600 border-purple-200 cursor-pointer hover:bg-purple-50" onClick={() => navigate('/contratti')}>
                            <FileText className="h-3 w-3" /> Contratto
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Importo */}
                    <div className="shrink-0 text-right min-w-[90px]">
                      {(deadline.importo || 0) > 0 ? (
                        <span className={`text-base font-bold ${
                          deadline.completata ? 'text-emerald-500 line-through'
                          : isOverdue(deadline.data_scadenza) ? 'text-red-600'
                          : 'text-indigo-700'
                        }`}>
                          {formatCurrency(deadline.importo)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </div>

                    {/* Azioni */}
                    <div className="flex items-center gap-1 shrink-0">
                      {deadline.completata && (
                        <Button variant="ghost" size="icon" className="text-slate-300 hover:text-amber-500" title="Riapri" onClick={() => handleReopen(deadline.id)}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      {!deadline.domain_id && !deadline.contract_id && !deadline.completata && (
                        <Button variant="ghost" size="icon" className="text-slate-300 hover:text-indigo-600" title="Modifica" onClick={() => handleEdit(deadline)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {(deadline.domain_id || deadline.contract_id) ? (
                        <Button variant="ghost" size="icon" className="text-slate-200 cursor-not-allowed" disabled
                          title={deadline.domain_id ? 'Gestisci dalla pagina Domini' : 'Gestisci dalla pagina Contratti'}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="text-slate-300 hover:text-red-500" onClick={() => handleDelete(deadline.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

      </div>
    </Layout>
  );
};
